import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";
import { tableFromIPC } from "apache-arrow";
import { getPolConfig } from "./polAggregators";
import { schemaTypeFor } from "./schemaTypes";
import { Box, Paper, Stack, Typography } from "@mui/material";

/* -------------------------------------------------------------------------- */
/* DATA HELPERS                                                               */
/* -------------------------------------------------------------------------- */

const inflight = new Map();

function rowsFromTable(table) {
  const rows = [];
  for (let i = 0; i < table.numRows; i++) {
    const row = {};
    for (const field of table.schema.fields) {
      const name = field.name;
      row[name] = table.getChild(name).get(i);
    }
    rows.push(row);
  }
  return rows;
}

export async function fetchMetrics({
  level,
  areaCodes = [],
  groupKeys = [],
  filters = {},
  theme = "pol",
  schema,
}) {
  const key = `${level}:${areaCodes.join(",")}:${groupKeys.join(",")}:${JSON.stringify(filters)}:${theme}:${schema || ""}`;
  if (inflight.has(key)) return inflight.get(key);

  const p = (async () => {
    const body = {
      theme,
      groupKeys,
      filters,
      areaCodes: areaCodes?.length ? areaCodes : [],
      schema,
      schema_type: schemaTypeFor(schema),
    };

    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error(`metrics ${res.status}`);

    const buf = await res.arrayBuffer();
    const rows = rowsFromTable(tableFromIPC(buf));
    return rows;
  })();

  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}

export async function fetchNational({
  groupKeys = [],
  filters = {},
  theme = "pol",
  schema,
}) {
  try {
    const natRows = await fetchMetrics({
      level: "national",
      areaCodes: [],
      groupKeys,
      filters,
      theme,
      schema,
    });
    if (natRows?.length) return natRows;
  } catch {
    console.warn("/api/national_data_arrow/ not found, averaging all districts");
  }

  const allRows = await fetchMetrics({
    level: "cd",
    areaCodes: [],
    groupKeys,
    filters,
    theme,
    schema,
  });

  if (!allRows?.length) return [];

  const avgRow = {};
  for (const key of groupKeys) {
    const vals = allRows.map((r) => r[key]).filter((v) => typeof v === "number" && isFinite(v));
    avgRow[key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }

  return [avgRow];
}

export async function loadGroupData({ groupId, areaCode, filters = {} }) {
  const { groupsById } = getPolConfig();
  const group = groupsById[groupId];
  if (!group) throw new Error(`Group '${groupId}' not found`);

  const groupKeys = group.keys || [];
  const labels = group.labels || {};
  const colors = group.colors || {};
  const schema = group.schema || {};

  const [rowsArea, rowsNat] = await Promise.all([
    fetchMetrics({ level: "cd", areaCodes: [String(areaCode)], groupKeys, filters, schema }),
    fetchNational({ groupKeys, filters, schema }),
  ]);

  const rowArea =
    rowsArea?.find((r) => String(r.area_id) === String(areaCode)) ||
    rowsArea?.[0] ||
    null;

  const rowNat = rowsNat?.[0] || null;

  return (groupKeys || []).map((k) => ({
    key: k,
    label: labels[k] || k,
    district: rowArea ? rowArea[k] ?? null : null,
    national: rowNat ? rowNat[k] ?? null : null,
    color: colors[k] || "#888",
  }));
}

export function getTopVI(data = []) {
  if (!Array.isArray(data) || !data.length) return null;
  const best = [...data]
    .filter((d) => Number.isFinite(d?.district))
    .sort((a, b) => (b.district ?? -Infinity) - (a.district ?? -Infinity))[0];
  return best || null;
}

/* -------------------------------------------------------------------------- */
/* CHARTS                                                                     */
/* -------------------------------------------------------------------------- */

export function BarCompareConstNat({
  data = [],
  title,
  areaName,
  stateName,
  nationalLabel = "National",
  height = 450,
  showNational = true,
}) {
  const areaLabel = areaName || "District";
  const stateLabel = stateName || "State";

  const sorted = useMemo(() => {
    return [...data]
      .filter((d) =>
        Number.isFinite(d.district) ||
        Number.isFinite(d.state) ||
        (showNational && Number.isFinite(d.national))
      )
      .sort((a, b) => (b.district ?? b.state ?? b.national ?? -Infinity) - (a.district ?? a.state ?? a.national ?? -Infinity))
      .slice(0, 10);
  }, [data, showNational]);

  const yAxisWidth = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = "12px Arial, Helvetica, sans-serif";
    const max = sorted.reduce((acc, d) => Math.max(acc, ctx.measureText(d.label).width), 0);
    return Math.min(180, Math.max(70, Math.ceil(max) + 16));
  }, [sorted]);

  const minVal = useMemo(
    () =>
      Math.min(
        0,
        ...sorted.flatMap((d) => [d.district ?? 0, d.state ?? 0, d.national ?? 0])
      ),
    [sorted]
  );

  const maxVal = useMemo(
    () =>
      Math.max(
        ...sorted.flatMap((d) => [d.district ?? 0, d.state ?? 0, d.national ?? 0])
      ),
    [sorted]
  );

  const TickWrap = ({ x, y, payload }) => {
    const text = payload?.value ?? "";
    const maxWidth = 90;
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0] || "";

    for (let i = 1; i < words.length; i++) {
      const testLine = `${currentLine} ${words[i]}`;
      if (testLine.length * 6 < maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = words[i];
      }
    }
    lines.push(currentLine);

    return (
      <text
        x={x - 6}
        y={y}
        textAnchor="end"
        fontSize={dynamicFontSize}
        fill="#444"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        {lines.map((line, index) => (
          <tspan key={index} x={x - 6} dy={index === 0 ? 0 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  const baseFontSize = 12;
  const minFontSize = 8;
  const availablePerBar = sorted.length ? height / sorted.length : height;
  const dynamicFontSize =
    availablePerBar < 20
      ? Math.max(minFontSize, (availablePerBar / 20) * baseFontSize)
      : baseFontSize;

  return (
    <div style={{ width: "100%", height }}>
      {title && (
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.95rem" }}>
          {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={sorted}
          layout="vertical"
          margin={{ top: 6, right: 40, left: 0, bottom: 8 }}
        >
          <XAxis type="number" domain={[minVal, maxVal]} hide />
          <YAxis type="category" dataKey="label" width={yAxisWidth - 25} tick={<TickWrap />} />
          <CartesianGrid horizontal={false} strokeDasharray="3 3" opacity={0.2} />
          <Tooltip
            formatter={(v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`)}
            labelStyle={{ fontWeight: 600 }}
            wrapperStyle={{ zIndex: 999999, pointerEvents: "none" }}
          />
          {showNational && (
            <Bar dataKey="national" barSize={10} name={nationalLabel}>
              {sorted.map((row, i) => (
                <Cell key={`n-${i}`} fill={row.color} fillOpacity={0.25} />
              ))}
            </Bar>
          )}
          <Bar dataKey="state" barSize={10} name={stateLabel}>
            {sorted.map((row, i) => (
              <Cell key={`s-${i}`} fill={row.color} fillOpacity={0.55} />
            ))}
          </Bar>
          <Bar dataKey="district" barSize={10} name={areaLabel}>
            <LabelList
              dataKey="district"
              position="right"
              formatter={(v) => (v == null || isNaN(v) ? "" : `${Number(v).toFixed(1)}%`)}
              style={{ fill: "#555", fontSize: dynamicFontSize * 0.9 }}
              offset={4}
            />
            {sorted.map((row, i) => (
              <Cell key={`c-${i}`} fill={row.color} fillOpacity={0.9} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BestPMGrid({ data = [], title, showTitle = false }) {
  const top = [...data]
    .filter((d) => Number.isFinite(d?.district))
    .sort((a, b) => (b.district ?? -Infinity) - (a.district ?? -Infinity))
    .slice(0, 4);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {showTitle && (
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, borderBottom: "2px solid #5FE1E6", display: "inline-block", pb: 0.3 }}>
          {title || "Best President"}
        </Typography>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, flexGrow: 1 }}>
        {top.map((d, i) => (
          <Paper key={i} elevation={0} sx={{ p: 1.2, borderRadius: 2, border: "1px solid #e5e7eb" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {d.label}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {Number.isFinite(d.district) ? `${d.district.toFixed(1)}%` : "—"}
              {Number.isFinite(d.national) ? ` (Nat ${d.national.toFixed(1)}%)` : ""}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export function TopThreePrioritiesMini({ data = [], height = 170 }) {
  const top3 = [...data]
    .filter((d) => Number.isFinite(d?.district) && Math.abs(d.district) > 1e-6)
    .sort((a, b) => (b.district ?? -Infinity) - (a.district ?? -Infinity))
    .slice(0, 3);

  return (
    <Box sx={{ height, width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      {top3.map((d, i) => (
        <Paper key={i} elevation={0} sx={{ p: 1, borderRadius: 2, border: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {d.label}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {d.district?.toFixed(1)}%
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}
